/* @jsx h */
import { defineComponent, h, computed } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import { getRewardText } from "@lemmonhq/orders";
import type { CoinOffer } from "@app/server/client";

/** Fallback glyph when a program has no icon, or an icon we do not recognise. */
const DEFAULT_COIN_ICON = "toll";

const KNOWN_ICONS = [
  "coffee",
  "local_bar",
  "wine_bar",
  "restaurant",
  "dining",
  "dine_lamp",
  "dinner_dining",
  "lunch_dining",
  "ramen_dining",
  "soba",
];

export const coinIconOf = (icon: string | null | undefined) =>
  icon && KNOWN_ICONS.includes(icon) ? icon : DEFAULT_COIN_ICON;

/**
 * One program's worth of coins, drawn as a stack of stamps.
 *
 * Up to `MAX_DOTS` are drawn individually — a row of stamps reads as "a loyalty
 * card" in a way a number does not. Past that it falls back to a count, because
 * thirty dots reads as noise.
 */
const MAX_DOTS = 10;

export const CoinStack = defineComponent<{ offer: CoinOffer }>({
  name: "CoinStack",
  props: ["offer"],
  setup(props) {
    return () => {
      const icon = coinIconOf(props.offer.icon);
      if (props.offer.amount > MAX_DOTS) {
        return (
          <div class="flex items-center gap-2">
            <span class="material-symbols-outlined text-3xl text-primary">
              {icon}
            </span>
            <span class="text-3xl font-bold text-primary">
              ×{props.offer.amount}
            </span>
          </div>
        );
      }
      return (
        <div class="flex flex-wrap gap-1.5">
          {Array.from({ length: props.offer.amount }).map((_, i) => (
            <span
              key={i}
              class="w-9 h-9 rounded-full bg-primary/15 flex items-center justify-center"
            >
              <span class="material-symbols-outlined text-xl text-primary">
                {icon}
              </span>
            </span>
          ))}
        </div>
      );
    };
  },
});

const bar = (classes: string) => (
  <div class={`rounded skeleton-shimmer ${classes}`} />
);

/**
 * Holds the offer's place while it loads. Shaped like the real card — eyebrow,
 * headline, subtitle, stamp row, CTA — so the layout does not jump when the
 * numbers arrive.
 */
export const CoinOfferSkeleton = defineComponent({
  name: "CoinOfferSkeleton",
  setup() {
    return () => (
      <div
        class="bg-base-100 rounded-2xl border border-base-300 p-6 space-y-4"
        aria-busy="true"
      >
        {bar("h-3 w-32")}
        {bar("h-9 w-40")}
        {bar("h-4 w-48 max-w-full")}
        <div class="flex gap-1.5">
          {[0, 1, 2].map((i) => (
            <div key={i} class="w-9 h-9 rounded-full skeleton-shimmer" />
          ))}
        </div>
        {bar("h-12 w-full")}
      </div>
    );
  },
});

/** One icon-led line. Used for both the benefit list and the how-it-works steps. */
const IconRow = defineComponent<{
  icon: string;
  title: string;
  detail?: string;
  step?: number;
}>({
  name: "CoinIconRow",
  props: ["icon", "title", "detail", "step"],
  setup(props) {
    return () => (
      <div class="flex items-start gap-3">
        <div class="relative shrink-0">
          <div class="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
            <span class="material-symbols-outlined text-lg text-primary">
              {props.icon}
            </span>
          </div>
          {props.step !== undefined && (
            <span class="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-primary text-primary-content text-[10px] font-bold flex items-center justify-center">
              {props.step}
            </span>
          )}
        </div>
        <div class="min-w-0 pt-0.5">
          <p class="text-sm font-medium text-base-content leading-snug">
            {props.title}
          </p>
          {props.detail && (
            <p class="text-xs text-base-content/50 mt-0.5 leading-snug">
              {props.detail}
            </p>
          )}
        </div>
      </div>
    );
  },
});

/**
 * Why bother creating an account. Shown under the offer, because the offer alone
 * answers "what do I get" but not "is this worth thirty seconds".
 */
export const CoinBenefits = defineComponent<{ validityMonths?: number | null }>({
  name: "CoinBenefits",
  props: ["validityMonths"],
  setup(props) {
    const { t } = useLocalization();
    return () => (
      <div class="space-y-3">
        <IconRow
          icon="qr_code_scanner"
          title={t("customer.coins.benefit_scan_title")}
          detail={t("customer.coins.benefit_scan_detail")}
        />
        <IconRow
          icon="savings"
          title={t("customer.coins.benefit_addup_title")}
          detail={t("customer.coins.benefit_addup_detail")}
        />
        <IconRow
          icon="bolt"
          title={t("customer.coins.benefit_fast_title")}
          detail={t("customer.coins.benefit_fast_detail")}
        />
        {props.validityMonths ? (
          <IconRow
            icon="schedule"
            title={t("customer.coins.benefit_validity_title", [
              props.validityMonths,
            ])}
            detail={t("customer.coins.benefit_validity_detail")}
          />
        ) : null}
      </div>
    );
  },
});

export type CoinOfferCardProps = {
  offers: CoinOffer[];
  shopName?: string;
  /** Rendered under the offer — the call to action, or a confirmation. */
  footer?: () => any;
  /** Past tense: the coins are already in an account. */
  collected?: boolean;
};

/**
 * The offer, stated before anything is asked of the reader.
 *
 * This card is the whole reason the claim flow exists: coins are minted at claim,
 * so until someone creates an account these coins do not exist anywhere. The
 * value has to land before the form does.
 */
export const CoinOfferCard = defineComponent<CoinOfferCardProps>({
  name: "CoinOfferCard",
  props: ["offers", "shopName", "footer", "collected"],
  setup(props, { slots }) {
    const { t } = useLocalization();

    const total = computed(() =>
      props.offers.reduce((sum, o) => sum + o.amount, 0),
    );

    return () => {
      if (props.offers.length === 0) return null;

      const single = props.offers.length === 1 ? props.offers[0] : null;

      return (
        <div class="rounded-2xl bg-gradient-to-br from-primary/10 via-base-100 to-base-100 border border-primary/20 shadow-sm overflow-hidden">
          <div class="p-5 sm:p-6 space-y-4">
            {/* The headline carries the count alone, never "2 stamp": coin_label
                is stored singular by design and t() has no pluralisation, so the
                merchant's word goes on its own line beside the program name. */}
            <div class="flex items-start justify-between gap-3">
              <div class="min-w-0">
                <p class="text-xs uppercase tracking-wider font-semibold text-primary/70">
                  {props.collected
                    ? t("customer.coins.collected_eyebrow")
                    : t("customer.coins.offer_eyebrow")}
                </p>
                <h2 class="text-2xl sm:text-3xl font-bold text-base-content mt-1 leading-tight">
                  {t(
                    props.collected
                      ? "customer.coins.collected_headline"
                      : "customer.coins.offer_headline",
                    [total.value],
                  )}
                </h2>
                <p class="text-sm text-base-content/60 mt-1">
                  {single
                    ? props.shopName
                      ? t("customer.coins.program_at_shop", [
                          single.name,
                          props.shopName,
                        ])
                      : single.name
                    : props.shopName
                      ? t("customer.coins.at_shop", [props.shopName])
                      : ""}
                </p>
              </div>
              {props.collected && (
                <span class="material-symbols-outlined text-3xl text-success shrink-0">
                  check_circle
                </span>
              )}
            </div>

            <div class="space-y-3">
              {props.offers.map((offer) => {
                // The cheapest tier: what these coins are working towards.
                // "Collect 3 stamps" is a weaker offer than "…towards a free
                // coffee", and the merchant has already configured the answer.
                const tier = (offer.tiers ?? [])[0];
                return (
                  <div key={offer.coin_program_id} class="space-y-1.5">
                    {props.offers.length > 1 && (
                      <p class="text-sm font-medium text-base-content/70">
                        {offer.name}
                      </p>
                    )}
                    <div class="flex items-center gap-3 flex-wrap">
                      <CoinStack offer={offer} />
                      <span class="text-sm text-base-content/50">
                        {offer.amount} × {offer.coin_label}
                      </span>
                    </div>
                    {tier && (
                      <div class="flex items-start gap-2 text-sm">
                        <span class="material-symbols-outlined text-base text-primary shrink-0">
                          redeem
                        </span>
                        <p class="text-base-content/70">
                          <span class="font-medium text-primary">
                            {getRewardText(tier.reward, t)}
                          </span>
                          <span class="text-base-content/50">
                            {" · "}
                            {t("customer.coins.tier_cost", [
                              tier.amount,
                              offer.coin_label,
                            ])}
                          </span>
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {(props.footer || slots.default) && (
              <div class="pt-1">
                {props.footer ? props.footer() : slots.default?.()}
              </div>
            )}
          </div>
        </div>
      );
    };
  },
});
